import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import {
  B2GO_PROMO_END,
  B2GO_PROMO_END_LABEL,
  B2GO_PROMO_RANGE_LABEL,
} from "constants/influencer";

const money = (n: number) => "$" + n.toFixed(2);

const pad = (n: number) => String(n).padStart(2, "0");

const useCountdown = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  let diff = Math.max(0, B2GO_PROMO_END.getTime() - now);
  const d = Math.floor(diff / 86_400_000);
  diff -= d * 86_400_000;
  const h = Math.floor(diff / 3_600_000);
  diff -= h * 3_600_000;
  const m = Math.floor(diff / 60_000);
  diff -= m * 60_000;
  const s = Math.floor(diff / 1_000);
  return [
    { n: d, l: "days" },
    { n: h, l: "hrs" },
    { n: m, l: "min" },
    { n: s, l: "sec" },
  ];
};

const Countdown = ({ compact = false }: { compact?: boolean }) => {
  const units = useCountdown();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: compact ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      {units.map((u, i) => (
        <Box
          key={u.l}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: compact ? 0.5 : 1,
          }}
        >
          <Box sx={{ textAlign: "center", minWidth: compact ? 28 : 52 }}>
            <Box
              sx={{
                fontWeight: 700,
                fontSize: compact ? 16 : 26,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
                borderRadius: compact ? "8px" : "12px",
                px: compact ? 0.5 : 0.75,
                pt: compact ? 0.5 : 1.25,
                pb: compact ? 0.4 : 1,
                backgroundColor: "rgba(255,255,255,0.1)",
              }}
            >
              {pad(u.n)}
            </Box>
            <Box
              sx={{
                mt: compact ? 0.4 : 0.75,
                fontSize: compact ? 8 : 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              {u.l}
            </Box>
          </Box>
          {i < units.length - 1 && (
            <Box
              sx={{
                fontWeight: 700,
                fontSize: compact ? 13 : 22,
                opacity: 0.5,
                mt: compact ? 0.4 : 1,
              }}
            >
              :
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export const B2GOPromoBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        borderRadius: 4,
        mb: { xs: 2, md: 3 },
        px: { xs: 2, md: 3.25 },
        py: { xs: 1.25, md: 2.5 },
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.5, md: 2.75 },
        flexWrap: { xs: "nowrap", md: "wrap" },
        backgroundColor: "primary.main",
        color: "#f7f6f0",
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            width: 52,
            height: 52,
            flexShrink: 0,
            borderRadius: "14px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            backgroundColor: "rgba(247,238,111,0.18)",
            color: "yellow.main",
          }}
        >
          <Box component="i" className="fa-solid fa-gift" />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: { xs: 0, md: 260 } }}>
        {!isMobile && (
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              opacity: 0.85,
            }}
          >
            ✨ Limited time · 1 week only
          </Box>
        )}
        <Typography
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: { xs: 14, md: 22 },
            lineHeight: 1.2,
            mt: { xs: 0, md: 0.5 },
          }}
        >
          {isMobile ? (
            <>
              Buy 2, get{" "}
              <Box component="b" sx={{ color: "yellow.main" }}>
                1 free
              </Box>{" "}
              🎁
            </>
          ) : (
            <>
              Buy 2 months, get your{" "}
              <Box component="b" sx={{ color: "yellow.main" }}>
                3rd month free
              </Box>{" "}
              🎁
            </>
          )}
        </Typography>
        {!isMobile && (
          <Typography
            sx={{ color: "white", fontSize: 12.5, opacity: 0.82, mt: 0.6 }}
          >
            Promo runs {B2GO_PROMO_RANGE_LABEL}
          </Typography>
        )}
      </Box>
      <Countdown compact={isMobile} />
    </Box>
  );
};

export const B2GOCallout = ({ monthlyBase }: { monthlyBase: number }) => (
  <Box
    sx={{
      mt: 1.75,
      borderRadius: 2,
      px: 1.75,
      py: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      fontSize: 13,
      backgroundColor: "rgba(247,238,111,0.14)",
      color: "white",
    }}
  >
    <Box
      component="i"
      className="fa-solid fa-gift"
      sx={{ color: "yellow.main", fontSize: 16 }}
    />
    <Box component="span">
      Pay for{" "}
      <Box component="b" sx={{ color: "yellow.main" }}>
        2 months ({money(monthlyBase * 2)})
      </Box>
      , your{" "}
      <Box component="b" sx={{ color: "yellow.main" }}>
        3rd month is free
      </Box>{" "}
      — that's {money(monthlyBase)} saved.
    </Box>
  </Box>
);

export const B2GOPromoLine = () => (
  <Typography
    sx={{
      mt: 1.5,
      fontSize: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      color: "text.secondary",
    }}
  >
    <Box component="span">✨</Box>
    <Box component="span">
      <Box component="strong" sx={{ color: "#b9a311" }}>
        Get 1 month free
      </Box>{" "}
      · ends {B2GO_PROMO_END_LABEL}
    </Box>
  </Typography>
);
